import React from "react";
import { ChevronDown, Eye, Loader2, Play, Pause, ShieldCheck, CalendarX } from "lucide-react";
import { formatDateTimeET } from "../../utils/datetime";
import { getSignalBadge, getInformativeSignalBadge } from "./StatusBadges";
import StrategySymbolChart from "./StrategySymbolChart";
import Modal from "../Modal";
import StrategyConfigPanel from "../StrategyConfigPanel";
import { hasFreshLevels, isValidPriceLevel } from "./strategyStatusUtils";
import LiveStrategyPrice from "./LiveStrategyPrice";

interface ONStrategyContentProps {
    onStatus: any;
    handleStrategyToggle: (strategyName: "ON" | "ORB" | "DT", currentlyEnabled: boolean | undefined) => Promise<void>;
    strategyToggleLoading: Partial<Record<"ON" | "ORB" | "DT", "starting" | "pausing">>;
}

const ONStrategyContent: React.FC<ONStrategyContentProps> = ({
    onStatus,
    handleStrategyToggle,
    strategyToggleLoading,
}) => {
    const [showRules, setShowRules] = React.useState(false);
    const [expandedSymbol, setExpandedSymbol] = React.useState<string | null>(null);
    const [showConfigModal, setShowConfigModal] = React.useState(false);
    if (!onStatus) return null;

    const hasData = !!onStatus.data;
    const onData = onStatus.data;
    const latestOnTimestamp =
        onData && onData.timestamp
            ? formatDateTimeET(onData.timestamp)
            : null;

    const onEnabled = hasData ? onStatus.data?.enabled : false;
    const toggleState = strategyToggleLoading.ON;
    const onPositions = (onData as any)?.positions || {};
    const onPositionEntries = Object.entries(onPositions).filter(
        ([, qty]) => typeof qty === "number" && qty !== 0
    );
    const onPositionMeta = (onData as any)?.position || {};
    const symbolStatus = onData?.symbol_status || {};

    return (
        <>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted">Strategy</span>
                    <span className="font-semibold text-foreground">ON</span>
                    {onData?.version && (
                        <span className="px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] text-muted font-mono" title="Strategy Logic Version">
                            v{onData.version}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => handleStrategyToggle("ON", onEnabled)}
                    disabled={!!toggleState}
                    aria-busy={!!toggleState}
                    className={`min-w-[104px] justify-center px-3 py-1.5 rounded-lg font-semibold uppercase flex text-sm items-center gap-1 transition disabled:cursor-wait disabled:opacity-80 ${toggleState ? "bg-amber-400/10 text-amber-300 border border-amber-400/20" : onEnabled ? "bg-neon-soft text-neon" : "text-muted hover:text-foreground hover:bg-white/10"
                        }`}
                >
                    {toggleState ? <Loader2 size={14} className="animate-spin" /> : onEnabled ? <Pause size={14} /> : <Play size={14} />}
                    {toggleState === "starting" ? "Starting" : toggleState === "pausing" ? "Pausing" : onEnabled ? "Pause" : "Start"}
                </button>
            </div>

            <div className="text-foreground">
                <button
                    onClick={() => setShowRules(!showRules)}
                    className="flex items-center gap-2 mb-4 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[11px] font-black uppercase tracking-[0.2em] text-muted hover:text-white hover:bg-white/10 transition-all shadow-inner group"
                >
                    <div className={`p-1 rounded-md transition-all ${showRules ? 'bg-neon/20 text-neon' : 'bg-white/10 text-white/40 group-hover:text-white'}`}>
                        <ShieldCheck size={14} className={showRules ? 'animate-pulse' : ''} />
                    </div>
                    <span>{showRules ? 'Hide' : 'Show'} Strategy Conditions & Rules</span>
                    <div className={`ml-auto transition-transform duration-300 ${showRules ? 'rotate-180 text-neon' : ''}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                </button>

                {showRules && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3 shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="p-1.5 rounded-lg bg-neon/10 text-neon">
                                    <ShieldCheck size={16} />
                                </div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-white/90">Entry Conditions</h4>
                            </div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Price must be above <b className="text-white/80">SMA30</b> and <b className="text-white/80">SMA200</b></span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Entry window: <b className="text-white/80">15:55 - 16:00 ET</b></span>
                                </li>
                            </ul>
                        </div>

                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3 shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400">
                                    <CalendarX size={16} />
                                </div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-white/90">Market Closure Rules</h4>
                            </div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span><b className="text-amber-400/90">No Entry</b> if tomorrow is a <b className="text-white/80">Weekend</b> or <b className="text-white/80">Holiday</b></span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>Positions are liquidated at <b className="text-white/80">09:30 ET</b> next trading day</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
                <div className="mt-4 rounded-2xl bg-white/6 text-sm text-foreground">
                    <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-3 min-w-0">
                            <h3 className="text-sm font-semibold text-neon">Signal Analysis</h3>
                            {onData?.config_source && (
                                <button
                                    type="button"
                                    onClick={() => setShowConfigModal(true)}
                                    className={`inline-flex min-h-7 shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${onData.config_source === 'USER'
                                        ? 'bg-neon/10 text-neon border border-neon/20'
                                        : 'bg-white/5 text-muted border border-white/10'
                                        } hover:bg-white/10 hover:text-white`}
                                    title="View current ON strategy setup"
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                    <span className="whitespace-nowrap">{onData.config_source === 'USER' ? 'User Config' : 'System Default'}</span>
                                    {onData.config_version && <span className="whitespace-nowrap">v{onData.config_version}</span>}
                                </button>
                            )}
                        </div>
                        {latestOnTimestamp && (
                            <div className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 flex flex-wrap items-center gap-2 self-start sm:self-auto">
                                <span className="text-[10px] text-muted uppercase font-bold tracking-wider">Latest Data</span>
                                <span className="text-[11px] text-white font-semibold">{latestOnTimestamp}</span>
                            </div>
                        )}
                    </div>

                    <div className="overflow-x-auto scrollbar-thin rounded-xl border border-white/5 bg-surface">
                        <table className="min-w-full divide-y divide-white/10 text-[13px] text-foreground">
                            <thead className="bg-black/60">
                                <tr>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Symbol</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Signal</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Technical</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Trading Day</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Price</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">SMAs (S/L)</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Size</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Lev</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Reason</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {Object.keys(symbolStatus).length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-3 py-4 text-center text-muted">
                                            {hasData ? "Market closed or no scan data available" : "Strategy offline"}
                                            {onStatus.error && <div className="text-rose-400 mt-1">{onStatus.error}</div>}
                                        </td>
                                    </tr>
                                ) : (
                                    Object.keys(symbolStatus).map((symbol) => {
                                        const data = symbolStatus[symbol];
                                        const isExpanded = expandedSymbol === symbol;
                                        const currentPrice = Number(data?.current_price);
                                        const smaShort = Number(data?.sma_short);
                                        const smaLong = Number(data?.sma_long);
                                        const isSignalLevel = hasFreshLevels(data, onData?.levels_date || onData?.timestamp) &&
                                            isValidPriceLevel(currentPrice) &&
                                            isValidPriceLevel(smaShort) &&
                                            isValidPriceLevel(smaLong) &&
                                            currentPrice > smaShort &&
                                            currentPrice > smaLong;

                                        if (!data || Object.keys(data).length === 0) {
                                            return (
                                                <tr key={symbol} className="hover:bg-white/5 transition-colors">
                                                    <td className="px-3 py-2 font-medium text-foreground">{symbol}</td>
                                                    <td colSpan={8} className="px-3 py-2 text-muted italic">No data available</td>
                                                </tr>
                                            );
                                        }

                                        return (
                                            <React.Fragment key={symbol}>
                                            <tr
                                                className={`transition-colors cursor-pointer ${isSignalLevel ? "bg-emerald-500/15 hover:bg-emerald-500/20 ring-1 ring-inset ring-emerald-400/20" : "hover:bg-white/5"}`}
                                                onClick={() => setExpandedSymbol(isExpanded ? null : symbol)}
                                            >
                                                <td className="px-4 py-3 font-bold text-[14px] text-white">
                                                    <div className="flex items-center gap-2">
                                                        <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${isExpanded ? "rotate-180 text-neon" : ""}`} />
                                                        <div className="flex flex-col">
                                                            <span>{symbol}</span>
                                                            {data.execution_sec_type === 'FUT' && data.execution_display_symbol && (
                                                                <span className="flex items-center gap-1 text-[10px] text-neon/80 font-mono font-bold uppercase tracking-wider">
                                                                    <span>{data.execution_display_symbol}</span>
                                                                    {data.execution_price_delayed && (
                                                                        <span className="rounded border border-amber-400/20 bg-amber-400/10 px-1 text-[9px] text-amber-300">
                                                                            Delayed
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">{getInformativeSignalBadge(data.informative_signal)}</td>
                                                <td className="px-3 py-2">{getSignalBadge(data.technical_signal)}</td>
                                                <td className="px-3 py-2">{getSignalBadge(data.next_day_is_trading)}</td>
                                                <td className="px-4 py-3 text-right font-mono">
                                                    <LiveStrategyPrice
                                                        symbol={symbol}
                                                        evaluatedPrice={data.current_price}
                                                        evaluatedQuote={data.evaluated_quote}
                                                        initialLiveQuote={data.live_quote}
                                                        executionPrice={data.execution_price}
                                                        executionQuote={data.execution_quote}
                                                        executionLabel={data.execution_display_symbol}
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-white/80">
                                                    {data.sma_short && data.sma_long ? (
                                                        <>
                                                            <span className="text-emerald-400">${Number(data.sma_short).toFixed(2)}</span>
                                                            <span className="mx-1">/</span>
                                                            <span className="text-amber-400">${Number(data.sma_long).toFixed(2)}</span>
                                                        </>
                                                    ) : "N/A"}
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-white/80">
                                                    {data.calculated_size ?? "—"}
                                                    {data.execution_sec_type === 'FUT' && data.calculated_size !== undefined && (
                                                        <span className="ml-1 text-[10px] text-white/40">ct</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-white/80">
                                                    {data.leverage ? `${(data.leverage * 100).toFixed(0)}%` : "—"}
                                                </td>
                                                <td className="px-4 py-3 text-white/70 max-w-[200px] truncate" title={data.reason}>
                                                    {data.reason}
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={9} className="p-0 bg-black/20">
                                                        <StrategySymbolChart symbol={symbol} timeframe="1d" label="ON" strategy="ON" status={{ ...data, levels_date: data.levels_date || onData?.levels_date || onData?.timestamp }} />
                                                    </td>
                                                </tr>
                                            )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {onPositionEntries.length > 0 && (
                    <div className="mt-3 rounded-2xl bg-white/6 text-sm text-foreground">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-neon">Current Position</h3>
                            {latestOnTimestamp && (
                                <span className="text-[11px] text-muted">Latest Data: {latestOnTimestamp}</span>
                            )}
                        </div>

                        <div className="overflow-x-auto scrollbar-thin rounded-xl border border-white/5 bg-surface">
                            <table className="min-w-full divide-y divide-white/10 text-[13px] text-foreground">
                                <thead className="bg-black/60">
                                    <tr>
                                        <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Symbol</th>
                                        <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Quantity</th>
                                        <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Entry Price</th>
                                        <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Entry Time</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {onPositionEntries.map(([sym, qty]) => (
                                        <tr key={sym} className="hover:bg-white/5 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap font-bold text-[14px] text-white">{sym}</td>
                                            <td className="px-4 py-3 whitespace-nowrap font-medium">{qty as number}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {onPositionMeta?.entry_price
                                                    ? `$${Number(onPositionMeta.entry_price).toFixed(2)}`
                                                    : "N/A"}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-white/70">
                                                {onPositionMeta?.entry_time
                                                    ? formatDateTimeET(onPositionMeta.entry_time)
                                                    : "N/A"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
            <Modal
                isOpen={showConfigModal}
                onClose={() => setShowConfigModal(false)}
                title="ON Strategy Setup"
                width="max-w-4xl"
            >
                <StrategyConfigPanel strategyName="ON" readOnly />
            </Modal>
        </>
    );
};

export default ONStrategyContent;
